import {
  cloneElement,
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";

function classes(base: string, extra?: string): string {
  return [base, extra].filter(Boolean).join(" ");
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "ghost" | "quiet" | "danger";
  size?: "sm" | "lg";
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size, loading = false, className, disabled, type = "button", ...props },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      className={classes(
        "btn",
        [variant === "primary" ? "" : variant, size, loading ? "loading" : "", className]
          .filter(Boolean)
          .join(" "),
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    />
  );
});

type FieldControlProps = {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling";
};

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactElement<FieldControlProps>;
  className?: string;
  variant?: "default" | "checkbox";
}

export function Field({
  label,
  hint,
  error,
  children,
  className,
  variant = "default",
}: FieldProps): ReactElement {
  const generatedId = useId();
  const id = children.props.id ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [children.props["aria-describedby"], hintId, errorId]
    .filter(Boolean)
    .join(" ");
  const control = cloneElement(children, {
    id,
    "aria-describedby": describedBy || undefined,
    "aria-invalid": error ? true : children.props["aria-invalid"],
  });

  return (
    <div className={classes("field", className)}>
      {variant === "checkbox" ? (
        <label className="field-label field-checkbox-label">
          {control}
          <span>{label}</span>
        </label>
      ) : (
        <>
          <label className="field-label" htmlFor={id}>
            {label}
          </label>
          {control}
        </>
      )}
      {hint && (
        <span className="field-hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field-error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...props },
  ref,
) {
  return <input {...props} ref={ref} className={classes("input", className)} />;
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, ...props },
  ref,
) {
  return <select {...props} ref={ref} className={classes("select", className)} />;
});

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className, ...props },
  ref,
) {
  return <input {...props} ref={ref} type="checkbox" className={classes("checkbox", className)} />;
});

export interface DateTimeInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  type?: "date" | "time" | "datetime-local";
}

export const DateTimeInput = forwardRef<HTMLInputElement, DateTimeInputProps>(
  function DateTimeInput({ className, type = "datetime-local", ...props }, ref) {
    return (
      <input {...props} ref={ref} type={type} className={classes("datetime-input", className)} />
    );
  },
);
